/**
 * LegalReconfirmDialog (Jul-2026, User-Wunsch) — sanfter Hinweis-Dialog nach
 * dem Login für Händler, wenn AVV oder Händler-Nutzungsbedingungen (SaaS)
 * noch nicht bzw. nicht in der AKTUELLEN Version bestätigt sind.
 * Nicht blockierend: „Später" schließt für die laufende Sitzung; die Annahme
 * wird versioniert + mit Zeitstempel dokumentiert (lückenlose Rechtsdoku).
 */
import { useEffect, useState } from "react";
import { ShieldAlert, FileSignature, ExternalLink, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

const SESSION_KEY = "tw_legal_dialog_dismissed";

export default function LegalReconfirmDialog() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [read, setRead] = useState({});
    const [busy, setBusy] = useState("");

    const isDealer = user?.role === "b2b";
    useEffect(() => {
        if (!isDealer) return;
        if (sessionStorage.getItem(SESSION_KEY)) return;
        let alive = true;
        Promise.all([
            api.get("/privacy/avv").then((r) => r.data).catch(() => null),
            api.get("/privacy/dealer-terms").then((r) => r.data).catch(() => null),
        ]).then(([avv, terms]) => {
            if (!alive) return;
            const list = [];
            if (avv && !avv.accepted) {
                list.push({
                    key: "avv", icon: ShieldAlert, to: "/legal/avv", acceptPath: "/privacy/avv/accept",
                    label: "Auftragsverarbeitungsvertrag (AVV)",
                    isUpdate: !!avv.accepted_version, version: avv.version,
                });
            }
            if (terms && !terms.accepted) {
                list.push({
                    key: "terms", icon: FileSignature, to: "/legal/haendler-nutzungsbedingungen", acceptPath: "/privacy/dealer-terms/accept",
                    label: "Händler-Nutzungsbedingungen (SaaS-Lizenz)",
                    isUpdate: !!terms.accepted_version, version: terms.version,
                });
            }
            if (list.length) {
                setItems(list); setOpen(true);
                // Chatbot schließen, damit der Rechts-Dialog keine sichtbaren,
                // aber blockierten Chat-Buttons hinterlässt (Test-Befund 625).
                try { window.dispatchEvent(new CustomEvent("tw-close-chatbot")); } catch { /* */ }
            }
        });
        return () => { alive = false; };
    }, [isDealer]);

    if (!isDealer || items.length === 0) return null;

    const accept = async (it) => {
        setBusy(it.key);
        try {
            await api.post(it.acceptPath);
            toast.success(`${it.label} bestätigt (Fassung ${it.version})`);
            const rest = items.filter((x) => x.key !== it.key);
            setItems(rest);
            if (rest.length === 0) setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bestätigung fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    const later = () => {
        sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) later(); }}>
            <DialogContent className="max-w-lg rounded-sm" data-testid="legal-reconfirm-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-black">Rechtliche Bestätigung ausstehend</DialogTitle>
                    <DialogDescription className="text-xs">
                        {items.some((i) => i.isUpdate)
                            ? "Es liegt eine aktualisierte Fassung vor — bitte prüfen und neu bestätigen. Ihre Bestätigung wird versioniert und mit Zeitstempel dokumentiert."
                            : "Bitte prüfen und bestätigen Sie die folgenden Vereinbarungen. Ihre Bestätigung wird versioniert und mit Zeitstempel dokumentiert."}
                    </DialogDescription>
                </DialogHeader>
                <ul className="space-y-3">
                    {items.map((it) => (
                        <li key={it.key} className="border border-gray-200 rounded-sm p-3" data-testid={`legal-item-${it.key}`}>
                            <div className="flex items-center gap-2">
                                <it.icon className="w-4 h-4 text-amber-700 shrink-0" />
                                <span className="text-sm font-bold flex-1 min-w-0">{it.label}</span>
                                {it.isUpdate && (
                                    <span className="text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm shrink-0">Neue Fassung</span>
                                )}
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <a href={it.to} target="_blank" rel="noreferrer"
                                    onClick={() => setRead((s) => ({ ...s, [it.key]: true }))}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold underline text-blue-700"
                                    data-testid={`legal-view-${it.key}`}>
                                    <ExternalLink className="w-3 h-3" /> Fassung {it.version} ansehen
                                </a>
                                <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-700">
                                    <input type="checkbox" checked={!!read[it.key]}
                                        onChange={(e) => setRead((s) => ({ ...s, [it.key]: e.target.checked }))}
                                        data-testid={`legal-read-${it.key}`} />
                                    Gelesen &amp; einverstanden
                                </label>
                                <Button size="sm" onClick={() => accept(it)} disabled={!read[it.key] || busy === it.key}
                                    className="ml-auto h-7 bg-[#111827] hover:bg-black text-white rounded-sm text-[11px] font-bold"
                                    data-testid={`legal-accept-${it.key}`}>
                                    {busy === it.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                                    Jetzt bestätigen
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
                <DialogFooter>
                    <Button variant="outline" onClick={later} className="rounded-sm text-xs" data-testid="legal-later-btn">
                        Später (Erinnerung bleibt im Dashboard)
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
